/**
 * Feature Flags Provider
 *
 * Provider component that makes feature flags available throughout the application.
 *
 * Primary responsibilities:
 * - Initialize feature flags
 * - Provide flags context to components
 * - Support server and client rendering
 * - Handle iOS and mobile device specific cases
 * - Apply feature flag codes from URL parameters
 *
 * Dependencies:
 * - Flags SDK
 * - React
 */

'use client'

import { ReactNode, useEffect } from 'react'
import { useSearchParams } from 'next/navigation'
import { Provider, useSetAtom } from 'jotai'
import { flagOverridesAtom, appFlags, marketingFlags } from '@/atoms/featureFlagsAtoms'
import dynamic from 'next/dynamic'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

type NavigatorWithDeviceMemory = Navigator & {
  deviceMemory?: number
}

type FlagOverrideValue = boolean | number | string

type MarketingFlagState = {
  heroVariant: string
  showNewCta: boolean
  pricingStrategy: string
}

type AppFlagState = {
  newUI: boolean
  premium: boolean
  experimental: boolean
}

type FlagApplyModule = typeof import('@/lib/feature-flags/apply-flags')

const DEFAULT_MARKETING_FLAGS: MarketingFlagState = {
  heroVariant: 'default',
  showNewCta: false,
  pricingStrategy: 'default'
}

const DEFAULT_APP_FLAGS: AppFlagState = {
  newUI: false,
  premium: false,
  experimental: false
}

// Check if running on iOS device or low-end device
function getIsMobileOrLowEnd() {
  if (typeof navigator === 'undefined') {
    return false
  }

  const nav = navigator as NavigatorWithDeviceMemory

  return (
    /iPad|iPhone|iPod/.test(nav.userAgent) ||
    (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1) ||
    (typeof nav.deviceMemory === 'number' && nav.deviceMemory < 4) ||
    (Boolean(nav.hardwareConcurrency) && nav.hardwareConcurrency <= 4)
  )
}

const isMobileOrLowEnd = getIsMobileOrLowEnd()

// Only log in development mode
const isDev = process.env.NODE_ENV === 'development'

// Component to handle flag overrides from URL parameters
function FlagOverrideHandler() {
  const searchParams = useSearchParams()
  const setOverrides = useSetAtom(flagOverridesAtom)

  useEffect(() => {
    // Check for feature flag overrides in URL params
    // Format: flags=flag1:value1,flag2:value2
    const flagParam = searchParams?.get('flags')
    if (flagParam) {
      const flagOverrides: Record<string, FlagOverrideValue> = {}

      flagParam.split(',').forEach((pair) => {
        const [key, value] = pair.split(':')
        if (key && value !== undefined) {
          // Convert string values to appropriate types
          if (value === 'true') {
            flagOverrides[key] = true
          } else if (value === 'false') {
            flagOverrides[key] = false
          } else if (!isNaN(Number(value))) {
            flagOverrides[key] = Number(value)
          } else {
            flagOverrides[key] = value
          }
        }
      })

      // Apply the overrides
      setOverrides((prev) => ({
        ...prev,
        ...flagOverrides
      }))
    }
  }, [searchParams, setOverrides])

  return null
}

// Flag initialization component that handles errors gracefully and defers work
function FlagInitializer() {
  const setAppFlags = useSetAtom(appFlags)
  const setMarketingFlags = useSetAtom(marketingFlags)
  const { isFeatureEnabled, getStringValue } = useFeatureFlags()

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return

    // Use lower priority initialization for best UI performance
    const initFlags = () => {
      // Don't block the UI during initial load
      const deferredInit = () => {
        async function initializeFlags() {
          try {
            // Check if we're in a parameterized route with flag codes
            const path = window.location.pathname
            let flagsInitialized = false

            // Extract code from URL if available
            const codeMatch = path.match(/^\/(app-flags|)\/([^\/]+)/)
            if (codeMatch) {
              try {
                const [, routeType, code] = codeMatch

                // Import the necessary utilities with error handling and short timeout
                try {
                  const { applyAppFlags, applyMarketingFlags } = await Promise.race<
                    FlagApplyModule
                  >([
                    import('@/lib/feature-flags/apply-flags'),
                    new Promise<never>((_, reject) =>
                      setTimeout(() => reject(new Error('Import timed out')), 2000)
                    )
                  ])

                  // Apply flags based on route type
                  if (routeType === 'app-flags') {
                    applyAppFlags(code, setAppFlags)
                    flagsInitialized = true
                  } else {
                    applyMarketingFlags(code, setMarketingFlags)
                    flagsInitialized = true
                  }
                } catch (importError) {
                  isDev && console.warn('Error importing flag utilities:', importError)
                  // Continue to try standard initialization
                }
              } catch (parseError) {
                isDev && console.warn('Error parsing route code:', parseError)
              }
            }

            // If flags weren't initialized from URL parameters, fetch them from the provider
            if (!flagsInitialized) {
              // Keep timeouts short to prevent UI blocking
              const timeoutPromise = (ms: number) =>
                new Promise<never>((_, reject) =>
                  setTimeout(() => reject(new Error('Flag retrieval timed out')), ms)
                )

              // Initialize flags with minimal operations
              try {
                // Use much shorter timeouts on mobile
                const timeoutDuration = isMobileOrLowEnd ? 500 : 1500

                const [
                  heroVariant,
                  showNewCta,
                  pricingStrategy,
                  newUI,
                  premium,
                  experimental
                ] = await Promise.all([
                  Promise.race([
                    getStringValue('hero-text-test', 'default'),
                    timeoutPromise(timeoutDuration)
                  ]).catch(() => DEFAULT_MARKETING_FLAGS.heroVariant),
                  Promise.race([
                    isFeatureEnabled('marketing-new-cta', false),
                    timeoutPromise(timeoutDuration)
                  ]).catch(() => DEFAULT_MARKETING_FLAGS.showNewCta),
                  Promise.race([
                    getStringValue('pricing-strategy', 'default'),
                    timeoutPromise(timeoutDuration)
                  ]).catch(() => DEFAULT_MARKETING_FLAGS.pricingStrategy),
                  Promise.race([
                    isFeatureEnabled('new-ui-component', false),
                    timeoutPromise(timeoutDuration)
                  ]).catch(() => DEFAULT_APP_FLAGS.newUI),
                  Promise.race([
                    isFeatureEnabled('premium-feature', false),
                    timeoutPromise(timeoutDuration)
                  ]).catch(() => DEFAULT_APP_FLAGS.premium),
                  Promise.race([
                    isFeatureEnabled('experimental-features', false),
                    timeoutPromise(timeoutDuration)
                  ]).catch(() => DEFAULT_APP_FLAGS.experimental)
                ])

                setMarketingFlags({
                  heroVariant,
                  showNewCta,
                  pricingStrategy
                })

                setAppFlags({
                  newUI,
                  premium,
                  experimental
                })
              } catch (error) {
                isDev && console.warn('Error fetching feature flags:', error)

                // Set default values on error
                setMarketingFlags(DEFAULT_MARKETING_FLAGS)
                setAppFlags(DEFAULT_APP_FLAGS)
              }
            }
          } catch (error) {
            isDev && console.error('Error initializing feature flags:', error)

            // Set default flag values to prevent app from breaking
            setMarketingFlags(DEFAULT_MARKETING_FLAGS)
            setAppFlags(DEFAULT_APP_FLAGS)
          }
        }

        // Start initialization but don't wait for it
        initializeFlags().catch((err) => {
          isDev && console.error('Uncaught error in feature flag initialization:', err)
        })
      }

      // For mobile/low-end devices, delay initialization even more
      // to prioritize UI rendering
      if (isMobileOrLowEnd) {
        setTimeout(deferredInit, 300)
      } else if ('requestIdleCallback' in window) {
        // Use requestIdleCallback for best performance on desktop
        window.requestIdleCallback(deferredInit, { timeout: 2000 })
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(deferredInit, 100)
      }
    }

    // Start initialization after a small delay
    setTimeout(initFlags, 0)
  }, [isFeatureEnabled, getStringValue, setAppFlags, setMarketingFlags])

  return null
}

// Support rendering in development - only loaded in dev mode
const FeatureFlagDevTools = isDev
  ? dynamic(() => import('./FeatureFlagDevTools'), {
      ssr: false,
      loading: () => null
    })
  : null

// Error boundary specifically for iOS/mobile devices
function ErrorFallback({ children }: { children: ReactNode }) {
  useEffect(() => {
    // Only needed for mobile devices
    if (!isMobileOrLowEnd) {
      return
    }

    // Add global error handler for unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Check if error is related to OpenFeature
      const errorMessage = String(event.reason || '')
      if (
        errorMessage.includes('feature flag') ||
        errorMessage.includes('OpenFeature') ||
        errorMessage.includes('Flagsmith')
      ) {
        isDev && console.warn('Caught feature flag error:', errorMessage)
        // Prevent it from crashing the app
        event.preventDefault()
      }
    }

    // Add event listener
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    // Set a general timeout to recover UI if needed
    const recoveryTimer = setTimeout(() => {
      if (document.body.classList.contains('error-state')) {
        document.body.classList.remove('error-state')
      }
    }, 2000)

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
      clearTimeout(recoveryTimer)
    }
  }, [])

  return <>{children}</>
}

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  return (
    <Provider>
      <FlagOverrideHandler />
      <ErrorFallback>
        {children}
        {/* Initialize flags after children to not block UI rendering */}
        <FlagInitializer />
        {isDev && FeatureFlagDevTools ? <FeatureFlagDevTools /> : null}
      </ErrorFallback>
    </Provider>
  )
}
